import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Plus, X } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { newslettersApi, signupFormsApi, SignupFormRecord, extractLookupId, NewsletterTagRow, NewsletterCategoryRuleRow, NewsletterMailingListRow } from '../services/creatorApi';
import { crmMailingListsApi, crmTagsApi, crmCategoriesApi, CrmMailingList, CrmTag, CrmCategory } from '../services/crmApi';
import { toast } from 'sonner';
export function NewsletterEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forms, setForms] = useState<SignupFormRecord[]>([]);
  const [mailingLists, setMailingLists] = useState<CrmMailingList[]>([]);
  const [tags, setTags] = useState<CrmTag[]>([]);
  const [categories, setCategories] = useState<CrmCategory[]>([]);
  // Form Data
  const [formData, setFormData] = useState({
    name: '',
    newsletterId: '',
    formId: '',
    mailingListIds: [] as string[],
    tagIds: [] as string[],
    categoryIds: [] as string[]
  });
  // Inline Create State
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creatingList, setCreatingList] = useState(false);
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  // Fetch Data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [formsData, listsData, tagsData, catsData] = await Promise.all([signupFormsApi.list(), crmMailingListsApi.list(), crmTagsApi.list(), crmCategoriesApi.list()]);
        // Filter forms by team so users only see their own team's forms
        const teamFilteredForms = formsData.filter(f => f.Team === currentWorkspace.name || f.Team === (currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '')));
        setForms(teamFilteredForms);
        setMailingLists(listsData);
        setTags(tagsData);
        setCategories(catsData);
        if (isEditMode && id) {
          const newsletter = await newslettersApi.get(id);
          if (newsletter) {
            const tagIds = (newsletter.Newsletter_Tags || []).map((t: NewsletterTagRow) => t.Tag_ID);
            const mailingListIds = (newsletter.Mailing_Lists || []).map((l: NewsletterMailingListRow) => l.Mailing_List_ID);
            // Parse existing category rules into simple category IDs
            const categoryIds = (newsletter.Newsletter_Category_Rules || []).map((r: NewsletterCategoryRuleRow) => r.Category_ID).filter(Boolean);
            const selectedFormId = extractLookupId(newsletter.Sign_Up_Form);
            setFormData({
              name: newsletter.Newsletter,
              newsletterId: newsletter.Newsletter_ID || '',
              formId: selectedFormId,
              mailingListIds,
              tagIds,
              categoryIds
            });
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, isEditMode]);
  // Handlers
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Newsletter name is required');
      return;
    }
    setSaving(true);
    try {
      // Build subform rows for Tags
      const tagRows: NewsletterTagRow[] = formData.tagIds.map(tagId => {
        const tag = tags.find(t => t.id === tagId);
        return tag ? {
          Tag_ID: tag.id,
          Tag_Name: tag.Name
        } : null;
      }).filter(Boolean) as NewsletterTagRow[];
      // Build subform rows for Mailing Lists
      const mailingListRows: NewsletterMailingListRow[] = formData.mailingListIds.map(listId => {
        const list = mailingLists.find(l => l.id === listId);
        return list ? {
          Mailing_List_ID: list.id,
          Mailing_List_Name: list.Name
        } : null;
      }).filter(Boolean) as NewsletterMailingListRow[];
      // Build subform rows for Categories (using the existing Newsletter_Category_Rules API names)
      const categoryRows: NewsletterCategoryRuleRow[] = formData.categoryIds.map(catId => {
        const cat = categories.find(c => c.id === catId);
        return cat ? {
          Form_Field: '',
          Condition: '',
          Value: '',
          Category_Name: cat.Name,
          Category_ID: cat.id
        } : null;
      }).filter(Boolean) as NewsletterCategoryRuleRow[];
      const newsletterId = formData.newsletterId || newslettersApi.generateId();
      const payload = {
        Newsletter: formData.name,
        Newsletter_ID: newsletterId,
        Sign_Up_Form: formData.formId,
        Team: currentWorkspace.name,
        Region: currentSubRegion || '',
        Mailing_Lists: mailingListRows,
        Newsletter_Tags: tagRows,
        Newsletter_Category_Rules: categoryRows
      };
      if (isEditMode && id) {
        await newslettersApi.update(id, payload);
        toast.success('Newsletter updated');
      } else {
        await newslettersApi.create(payload);
        toast.success('Newsletter created');
      }
      navigate('/newsletters');
    } catch (err) {
      console.error('Failed to save newsletter:', err);
      toast.error('Failed to save newsletter');
    } finally {
      setSaving(false);
    }
  };
  // Inline Create Handlers
  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    setCreatingList(true);
    try {
      await crmMailingListsApi.create({
        Name: newListName,
        Team: currentWorkspace.name,
        Region: currentSubRegion || undefined,
        Status: 'open'
      });
      const updatedLists = await crmMailingListsApi.list();
      setMailingLists(updatedLists);
      const newList = updatedLists.find(l => l.Name === newListName);
      if (newList) {
        setFormData(prev => ({
          ...prev,
          mailingListIds: [...prev.mailingListIds, newList.id]
        }));
      }
      setShowCreateList(false);
      setNewListName('');
      toast.success('Mailing list created');
    } catch (err) {
      toast.error('Failed to create mailing list');
    } finally {
      setCreatingList(false);
    }
  };
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    setCreatingTag(true);
    try {
      await crmTagsApi.create({
        Name: newTagName,
        Team: currentWorkspace.name
      });
      const updatedTags = await crmTagsApi.list();
      setTags(updatedTags);
      const newTag = updatedTags.find(t => t.Name === newTagName);
      if (newTag) {
        setFormData(prev => ({
          ...prev,
          tagIds: [...prev.tagIds, newTag.id]
        }));
      }
      setShowCreateTag(false);
      setNewTagName('');
      toast.success('Tag created');
    } catch (err) {
      toast.error('Failed to create tag');
    } finally {
      setCreatingTag(false);
    }
  };
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    setCreatingCategory(true);
    try {
      await crmCategoriesApi.create({
        Name: newCategoryName,
        Team: currentWorkspace.name
      });
      const updatedCats = await crmCategoriesApi.list();
      setCategories(updatedCats);
      const newCat = updatedCats.find(c => c.Name === newCategoryName);
      if (newCat) {
        setFormData(prev => ({
          ...prev,
          categoryIds: [...prev.categoryIds, newCat.id]
        }));
      }
      setShowCreateCategory(false);
      setNewCategoryName('');
      toast.success('Category created');
    } catch (err) {
      toast.error('Failed to create category');
    } finally {
      setCreatingCategory(false);
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-3543">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3544" />
      </div>;
  }
  return <div className="space-y-6 max-w-4xl pb-20" data-id="element-3545">
      <div className="flex items-center gap-4" data-id="element-3546">
        <Link to="/newsletters" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-3547">
          <ArrowLeft className="h-5 w-5" data-id="element-3548" />
        </Link>
        <div data-id="element-3549">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3550">
            {isEditMode ? 'Edit Newsletter' : 'Create Newsletter'}
          </h1>
          <p className="text-sm text-gray-500" data-id="element-3551">
            Configure audience lists and CRM integration.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8" data-id="element-3552">
        {/* Basic Info */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6" data-id="element-3553">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-3554">
            Basic Information
          </h2>
          <div className="grid grid-cols-1 gap-6" data-id="element-3555">
            <div data-id="element-3556">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3557">
                Newsletter Name
              </label>
              <input type="text" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} placeholder="e.g., Weekly Updates" data-id="element-3558" />
            </div>

            {isEditMode && formData.newsletterId && <div data-id="element-3559">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3560">
                  Newsletter ID
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed font-mono" value={formData.newsletterId} disabled data-id="element-3561" />
                <p className="mt-1 text-xs text-gray-400" data-id="element-3562">
                  Unique identifier used to link form submissions to this
                  newsletter.
                </p>
              </div>}

            <div data-id="element-3563">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3564">
                Sign Up Form
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.formId} onChange={e => setFormData({
              ...formData,
              formId: e.target.value
            })} data-id="element-3565">
                <option value="" data-id="element-3566">Select a form...</option>
                {forms.map(f => <option key={f.ID} value={f.ID} data-id="element-3567">
                    {f.Form_Name}
                  </option>)}
              </select>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2" data-id="element-3568">
              <h3 className="text-sm font-medium text-gray-900" data-id="element-3569">Scope</h3>
              <div className="text-sm text-gray-600 grid grid-cols-2 gap-4" data-id="element-3570">
                <div data-id="element-3571">
                  <strong data-id="element-3572">Team:</strong> {currentWorkspace.name}
                </div>
                <div data-id="element-3573">
                  <strong data-id="element-3574">Region:</strong> {currentSubRegion || 'All Regions'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CRM Integration */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6" data-id="element-3575">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-3576">CRM Integration</h2>

          {/* Mailing Lists (Multi-select) */}
          <div data-id="element-3577">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3578">
              Assign to Mailing Lists
            </label>
            <div className="space-y-2" data-id="element-3579">
              <div className="flex flex-wrap gap-2 mb-2" data-id="element-3580">
                {formData.mailingListIds.map(listId => {
                const list = mailingLists.find(l => l.id === listId);
                return list ? <span key={listId} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-blue-50 text-blue-700 border border-blue-200" data-id="element-3581">
                      {list.Name}
                      <button type="button" onClick={() => setFormData(prev => ({
                    ...prev,
                    mailingListIds: prev.mailingListIds.filter(id => id !== listId)
                  }))} className="hover:text-blue-900" data-id="element-3582">
                        <X className="h-3 w-3" data-id="element-3583" />
                      </button>
                    </span> : null;
              })}
              </div>
              <div className="flex gap-2" data-id="element-3584">
                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value="" onChange={e => {
                if (e.target.value && !formData.mailingListIds.includes(e.target.value)) {
                  setFormData(prev => ({
                    ...prev,
                    mailingListIds: [...prev.mailingListIds, e.target.value]
                  }));
                }
              }} data-id="element-3585">
                  <option value="" data-id="element-3586">Add a mailing list...</option>
                  {mailingLists.filter(l => !formData.mailingListIds.includes(l.id)).map(l => <option key={l.id} value={l.id} data-id="element-3587">
                        {l.Name}
                      </option>)}
                </select>
                <button type="button" onClick={() => setShowCreateList(true)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" title="Create new mailing list" data-id="element-3588">
                  <Plus className="h-4 w-4" data-id="element-3589" />
                </button>
              </div>
              {showCreateList && <div className="mt-2 flex gap-2 items-center bg-gray-50 p-3 rounded-md border border-gray-200" data-id="element-3590">
                  <input type="text" placeholder="New mailing list name" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newListName} onChange={e => setNewListName(e.target.value)} data-id="element-3591" />
                  <button type="button" onClick={handleCreateList} disabled={creatingList || !newListName.trim()} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-id="element-3592">
                    {creatingList ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-3593" /> : 'Create'}
                  </button>
                  <button type="button" onClick={() => setShowCreateList(false)} className="p-2 text-gray-400 hover:text-gray-600" data-id="element-3594">
                    <X className="h-4 w-4" data-id="element-3595" />
                  </button>
                </div>}
            </div>
          </div>

          {/* Tags (Multi-select) */}
          <div data-id="element-3596">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3597">
              Assign Tags
            </label>
            <div className="space-y-2" data-id="element-3598">
              <div className="flex flex-wrap gap-2 mb-2" data-id="element-3599">
                {formData.tagIds.map(tagId => {
                const tag = tags.find(t => t.id === tagId);
                return tag ? <span key={tagId} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-blue-50 text-blue-700 border border-blue-200" data-id="element-3600">
                      {tag.Name}
                      <button type="button" onClick={() => setFormData(prev => ({
                    ...prev,
                    tagIds: prev.tagIds.filter(id => id !== tagId)
                  }))} className="hover:text-blue-900" data-id="element-3601">
                        <X className="h-3 w-3" data-id="element-3602" />
                      </button>
                    </span> : null;
              })}
              </div>
              <div className="flex gap-2" data-id="element-3603">
                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value="" onChange={e => {
                if (e.target.value && !formData.tagIds.includes(e.target.value)) {
                  setFormData(prev => ({
                    ...prev,
                    tagIds: [...prev.tagIds, e.target.value]
                  }));
                }
              }} data-id="element-3604">
                  <option value="" data-id="element-3605">Add a tag...</option>
                  {tags.filter(t => !formData.tagIds.includes(t.id)).map(t => <option key={t.id} value={t.id} data-id="element-3606">
                        {t.Name}
                      </option>)}
                </select>
                <button type="button" onClick={() => setShowCreateTag(true)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" title="Create new tag" data-id="element-3607">
                  <Plus className="h-4 w-4" data-id="element-3608" />
                </button>
              </div>
              {showCreateTag && <div className="mt-2 flex gap-2 items-center bg-gray-50 p-3 rounded-md border border-gray-200" data-id="element-3609">
                  <input type="text" placeholder="New tag name" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newTagName} onChange={e => setNewTagName(e.target.value)} data-id="element-3610" />
                  <button type="button" onClick={handleCreateTag} disabled={creatingTag || !newTagName.trim()} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-id="element-3611">
                    {creatingTag ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-3612" /> : 'Create'}
                  </button>
                  <button type="button" onClick={() => setShowCreateTag(false)} className="p-2 text-gray-400 hover:text-gray-600" data-id="element-3613">
                    <X className="h-4 w-4" data-id="element-3614" />
                  </button>
                </div>}
            </div>
          </div>

          {/* Categories (Multi-select — same pattern as tags) */}
          <div data-id="element-3615">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3616">
              Assign Categories
            </label>
            <div className="space-y-2" data-id="element-3617">
              <div className="flex flex-wrap gap-2 mb-2" data-id="element-3618">
                {formData.categoryIds.map(catId => {
                const cat = categories.find(c => c.id === catId);
                return cat ? <span key={catId} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-amber-50 text-amber-700 border border-amber-200" data-id="element-3619">
                      {cat.Name}
                      <button type="button" onClick={() => setFormData(prev => ({
                    ...prev,
                    categoryIds: prev.categoryIds.filter(id => id !== catId)
                  }))} className="hover:text-amber-900" data-id="element-3620">
                        <X className="h-3 w-3" data-id="element-3621" />
                      </button>
                    </span> : null;
              })}
              </div>
              <div className="flex gap-2" data-id="element-3622">
                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value="" onChange={e => {
                if (e.target.value && !formData.categoryIds.includes(e.target.value)) {
                  setFormData(prev => ({
                    ...prev,
                    categoryIds: [...prev.categoryIds, e.target.value]
                  }));
                }
              }} data-id="element-3623">
                  <option value="" data-id="element-3624">Add a category...</option>
                  {categories.filter(c => !formData.categoryIds.includes(c.id)).map(c => <option key={c.id} value={c.id} data-id="element-3625">
                        {c.Name}
                      </option>)}
                </select>
                <button type="button" onClick={() => setShowCreateCategory(true)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" title="Create new category" data-id="element-3626">
                  <Plus className="h-4 w-4" data-id="element-3627" />
                </button>
              </div>
              {showCreateCategory && <div className="mt-2 flex gap-2 items-center bg-amber-50 p-3 rounded-md border border-amber-100" data-id="element-3628">
                  <input type="text" placeholder="New category name" className="block w-full rounded-md border-amber-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} data-id="element-3629" />
                  <button type="button" onClick={handleCreateCategory} disabled={creatingCategory || !newCategoryName.trim()} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-id="element-3630">
                    {creatingCategory ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-3631" /> : 'Create'}
                  </button>
                  <button type="button" onClick={() => setShowCreateCategory(false)} className="p-2 text-amber-400 hover:text-amber-600" data-id="element-3632">
                    <X className="h-4 w-4" data-id="element-3633" />
                  </button>
                </div>}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-3634">
          <Link to="/newsletters" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3635">
            Cancel
          </Link>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-3636">
            {saving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-3637" />}
            Save Newsletter
          </button>
        </div>
      </form>
    </div>;
}